import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define the shape of the auth context
interface AuthContextType {
  isAuthenticated: boolean;
  user: { username: string } | null; // Replace with your actual user type
  token: string | null;
  isLoading: boolean; // Add isLoading
  login: (username: string, pass: string) => Promise<void>; // Pass instead of password to avoid linting issues
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>; // Function to check auth status on app load
}

// Create the context with a default undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define the props for the AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<{ id?: string; username: string } | null>(null); // User can have an id
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Default to true
  const BACKEND_URL_ROOT = process.env.REACT_APP_BACKEND_URL_ROOT || 'http://localhost:3001';

  // Check auth status on initial load from localStorage
  const checkAuth = async () => {
    // setIsLoading(true); // Already true on init, set false at the end
    console.log("Attempting to check auth status from localStorage...");
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
        console.log("Auth status restored from localStorage: isAuthenticated = true");
        // Optional: Verify token with backend here
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        // Clear potentially corrupted storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        console.log("Corrupted auth data cleared from localStorage.");
      }
    } else {
      console.log("No token or user found in localStorage.");
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      console.log("Auth status: isAuthenticated = false");
    }
    setIsLoading(false); // Set to false after logic is complete
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (username: string, pass: string) => {
    console.log(`Attempting API login for username: ${username}`);
    try {
      const response = await fetch(`${BACKEND_URL_ROOT}/auth/login`, { // Use BACKEND_URL_ROOT
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password: pass }), // Backend expects 'password'
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle HTTP errors (4xx, 5xx)
        console.error("Login API error:", data.message || `Status: ${response.status}`);
        throw new Error(data.message || `Login failed with status: ${response.status}`);
      }

      // Assuming successful login returns { token, user }
      if (data.token && data.user) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        console.log("Login successful, token and user stored. User:", data.user);
      } else {
        console.error("Login API response missing token or user:", data);
        throw new Error('Login failed: Invalid response from server.');
      }
    } catch (error: any) {
      console.error("Login function error:", error.message);
      // Ensure state is reset on error
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      throw error; // Re-throw to be caught by UI component if needed
    }
  };

  const logout = async () => {
    console.log("Attempting logout...");
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    console.log("Logout successful, token and user removed from localStorage and state.");
    // No API call needed for this simple client-side JWT logout.
    // If backend had session management or token blacklisting, an API call would be here.
    return Promise.resolve();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout, checkAuth, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a custom hook to use the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
